/**
 * Histórico dos prompts enviados pelo composer — o ↑/↓ de terminal.
 *
 * Global do USUÁRIO (não por conversa), como o Claude Code. Vive no
 * `localStorage` carimbado por escritório + profissional: é conveniência deste
 * navegador, não dado de negócio, e não vale endpoint. A lista é do mais
 * RECENTE (índice 0) para o mais antigo.
 *
 * Storage bloqueado (modo privado, quota) nunca derruba o composer: a
 * `memoria` do módulo segura a lista enquanto a aba viver — e é ela que faz o
 * herói e o rodapé (instâncias diferentes) enxergarem o mesmo histórico.
 *
 * Puro: storage entra por parâmetro. Sem `?.`/`??`.
 */
const PREFIXO_CHAVE = 'ia-historico-de-prompts'
const VERSAO = 1

export const TETO_DE_ITENS = 50
export const TETO_DE_CARACTERES = 4000

// Fora da navegação: o campo mostra o que o usuário digitou.
export const INDICE_FORA_DO_HISTORICO = -1

const memoria = {}

/** `null` sem profissional: histórico anônimo seria compartilhado entre pessoas. */
export const chaveDoHistorico = ({ idEscritorio, idProfissional } = {}) => {
  if (!idProfissional) return null
  return `${PREFIXO_CHAVE}:v${VERSAO}:${idEscritorio || 'na'}:${idProfissional}`
}

export const estadoInicialDaNavegacao = () => ({
  indice: INDICE_FORA_DO_HISTORICO,
  rascunho: '',
})

export const normalizarPrompt = (texto) =>
  String(texto || '').trim().slice(0, TETO_DE_CARACTERES)

/** Novo no topo; ignora vazio e repetição consecutiva idêntica; corta no teto. */
export const adicionarAoHistorico = (lista, texto) => {
  const atual = Array.isArray(lista) ? lista : []
  const prompt = normalizarPrompt(texto)
  if (!prompt || atual[0] === prompt) return atual
  return [prompt].concat(atual).slice(0, TETO_DE_ITENS)
}

/**
 * ↑: um passo para trás. Na primeira subida guarda o `valorAtual` como
 * rascunho. `null` quando não há para onde ir (lista vazia ou já no mais antigo).
 */
export const navegarParaAnterior = (lista, estado, valorAtual) => {
  const itens = Array.isArray(lista) ? lista : []
  const indice = Math.min(estado.indice + 1, itens.length - 1)
  if (indice < 0 || indice === estado.indice) return null
  const saindoDoCampo = estado.indice === INDICE_FORA_DO_HISTORICO
  const rascunho = saindoDoCampo ? String(valorAtual || '') : estado.rascunho
  return { estado: { indice, rascunho }, texto: itens[indice] }
}

/** ↓: um passo para frente; passando do mais recente, devolve o rascunho. */
export const navegarParaProximo = (lista, estado) => {
  if (estado.indice === INDICE_FORA_DO_HISTORICO) return null
  const itens = Array.isArray(lista) ? lista : []
  const indice = estado.indice - 1
  if (indice === INDICE_FORA_DO_HISTORICO || !itens[indice]) {
    return { estado: estadoInicialDaNavegacao(), texto: estado.rascunho }
  }
  return { estado: { indice, rascunho: estado.rascunho }, texto: itens[indice] }
}

function storagePadrao() {
  try {
    return globalThis.localStorage || null
  } catch {
    return null
  }
}

function lerDoStorage(storage, chave) {
  if (!storage) return null
  try {
    const dado = JSON.parse(storage.getItem(chave))
    if (!dado || dado.v !== VERSAO || !Array.isArray(dado.itens)) return null
    return dado.itens.filter((item) => typeof item === 'string')
  } catch {
    return null
  }
}

export function lerHistorico({ escopo, storage = storagePadrao() } = {}) {
  const chave = chaveDoHistorico(escopo)
  if (!chave) return []
  return lerDoStorage(storage, chave) || memoria[chave] || []
}

/** Registra o prompt enviado e devolve a lista resultante. */
export function registrarPrompt(texto, { escopo, storage = storagePadrao() } = {}) {
  const chave = chaveDoHistorico(escopo)
  if (!chave) return []
  const lista = adicionarAoHistorico(lerHistorico({ escopo, storage }), texto)
  memoria[chave] = lista
  if (!storage) return lista
  try {
    storage.setItem(chave, JSON.stringify({ v: VERSAO, itens: lista }))
  } catch {
    // storage indisponível: o histórico vale só nesta aba (memória).
  }
  return lista
}

/** Uso exclusivo de teste — zera a memória entre casos. */
export const resetarMemoriaDoHistorico = () => {
  Object.keys(memoria).forEach((chave) => delete memoria[chave])
}
